import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import client from './client';
import type { CheckInDto, StandupSummaryDto, PagedResult } from '../types/api';

export const checkInKeys = {
  all:     ()                                   => ['checkIns']              as const,
  history: (engineerId?: string, cursor?: string) => ['checkIns', 'history', engineerId, cursor] as const,
  status:  (date?: string)                      => ['checkIns', 'status', date] as const,
  standup: (teamId?: string, date?: string)     => ['checkIns', 'standup', teamId, date] as const,
};

export function useCheckInHistory(engineerId?: string, cursor?: string) {
  return useQuery({
    queryKey: checkInKeys.history(engineerId, cursor),
    queryFn: () =>
      client.get<PagedResult<CheckInDto>>('/check-ins', {
        params: { engineerId, cursor, limit: 20 },
      }).then((r) => r.data),
  });
}

export function useCheckInStatus(date?: string) {
  return useQuery({
    queryKey: checkInKeys.status(date),
    queryFn: () =>
      client.get<{ checkedIn: boolean; missingEngineers: string[] }>('/check-ins/status', {
        params: date ? { date } : undefined,
      }).then((r) => r.data),
  });
}

export function useStandupSummary(teamId?: string, date?: string) {
  return useQuery({
    queryKey: checkInKeys.standup(teamId, date),
    queryFn: () =>
      client.get<StandupSummaryDto>('/check-ins/standup', {
        params: { teamId, date },
      }).then((r) => r.data),
  });
}

export function useSubmitCheckIn() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { date: string; completed: string; plannedNext: string; blockers?: string }) =>
      client.post<CheckInDto>('/check-ins', body).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: checkInKeys.all() }),
  });
}
